/**
 * FormErrorSummary Component
 * Displays a scrollable list of all form errors with navigation links
 */
import React, { useMemo } from 'react';
import { FieldErrors, FieldValues } from 'react-hook-form';
import { AlertCircle, ChevronRight } from 'lucide-react';

export interface FormErrorSummaryProps<T extends FieldValues> {
  errors: FieldErrors<T>;
  formId?: string;
  className?: string;
}

/**
 * Flattens nested error objects into a flat array of error paths and messages
 */
function flattenErrors<T extends FieldValues>(
  errors: FieldErrors<T>,
  prefix = ''
): Array<{ path: string; message: string; fieldName: string }> {
  const result: Array<{ path: string; message: string; fieldName: string }> = [];

  for (const key in errors) {
    const error = errors[key];
    const currentPath = prefix ? `${prefix}.${key}` : key;

    if (error?.message && typeof error.message === 'string') {
      // Format field name for display (e.g., "entries.0.accountId" -> "Entry 1 - Account")
      const fieldName = formatFieldName(currentPath);
      result.push({
        path: currentPath,
        message: error.message,
        fieldName,
      });
    } else if (error && typeof error === 'object') {
      // Handle nested errors (e.g., entries[0].accountId)
      if (Array.isArray(error)) {
        error.forEach((item: unknown, index: number) => {
          if (item && typeof item === 'object') {
            result.push(...flattenErrors(item as FieldErrors<T>, `${currentPath}.${index}`));
          }
        });
      } else if (typeof error === 'object' && 'root' in error) {
        // Handle root-level errors - explicit check for root property existence
        const rootError = (error as Record<string, unknown>).root as { message?: string } | undefined;
        if (rootError?.message) {
          result.push({
            path: currentPath,
            message: rootError.message,
            fieldName: formatFieldName(currentPath),
          });
        }
      } else {
        result.push(...flattenErrors(error as FieldErrors<T>, currentPath));
      }
    }
  }

  return result;
}

/**
 * Formats field paths into human-readable names
 */
function formatFieldName(path: string): string {
  // Handle array indices (e.g., "entries.0.accountId" -> "Entry 1 - Account")
  const arrayMatch = path.match(/^(\w+)\.(\d+)\.(\w+)$/);
  if (arrayMatch) {
    const [, arrayName, index, fieldName] = arrayMatch;
    const entryNumber = Number.parseInt(index, 10) + 1;
    const formattedField = fieldName
      .replaceAll(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
    return `${arrayName.charAt(0).toUpperCase() + arrayName.slice(1)} ${entryNumber} - ${formattedField}`;
  }

  // Handle simple paths (e.g., "date" -> "Date")
  return path
    .split('.')
    .map((part) => part.replaceAll(/([A-Z])/g, ' $1').trim())
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' - ');
}

export function FormErrorSummary<T extends FieldValues>({
  errors,
  formId,
  className = '',
}: FormErrorSummaryProps<T>) {
  const errorList = useMemo(() => flattenErrors(errors), [errors]);
  const errorSummaryRef = React.useRef<HTMLDivElement>(null);

  // Auto-focus error summary when it first appears (for screen readers)
  React.useEffect(() => {
    if (errorList.length > 0 && errorSummaryRef.current && document.activeElement !== errorSummaryRef.current) {
      errorSummaryRef.current.focus();
    }
  }, [errorList.length]);

  if (errorList.length === 0) {
    return null;
  }

  const handleErrorClick = (path: string) => {
    // Try to find the field by various methods
    const fieldId = formId ? `${formId}-${path.replaceAll('.', '-')}` : path.replaceAll('.', '-');

    // Try multiple selectors
    const selectors = [
      `[name="${path}"]`,
      `#${fieldId}`,
      `[id="${fieldId}"]`,
      `[data-field="${path}"]`,
    ];

    let fieldElement: HTMLElement | null = null;
    for (const selector of selectors) {
      fieldElement = document.querySelector(selector);
      if (fieldElement) break;
    }

    if (fieldElement) {
      fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Focus the field if it's focusable
      if (fieldElement instanceof HTMLElement && 'focus' in fieldElement) {
        (fieldElement).focus();
      }
    }
  };

  return (
    <div
      ref={errorSummaryRef}
      role="alert"
      aria-live="polite"
      tabIndex={-1}
      className={`bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${className}`}
    >
      <div className="flex items-start">
        <AlertCircle
          size={20}
          className="text-red-600 dark:text-red-400 mr-3 mt-0.5 flex-shrink-0"
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-2">
            {errorList.length} {errorList.length === 1 ? 'error' : 'errors'} found
          </h3>
          <ul className="space-y-1.5 max-h-48 overflow-y-auto">
            {errorList.map((error, index) => (
              <li key={`${error.path}-${index}`}>
                <button
                  type="button"
                  onClick={() => handleErrorClick(error.path)}
                  className="text-sm text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 hover:underline flex items-center w-full text-left group"
                >
                  <span className="flex-1 min-w-0">
                    <span className="font-medium">{error.fieldName}:</span>{' '}
                    <span className="text-red-600 dark:text-red-400">{error.message}</span>
                  </span>
                  <ChevronRight
                    size={16}
                    className="ml-2 text-red-500 dark:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    aria-hidden="true"
                  />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
